<template>
  <div class="page-container">
    <LibrariesSkeletonLoader
      :is-loading="libraryLoading"
      show-three-dots-menu
      class="ml-6 mr-6"
    />
    <div v-if="!listLoading && !pageLoading">
      <NoFlagshipPosts
        v-if="!documents.length && documentType === 'flagship' && !activeFilters"
        class="ml-6 mr-6"
      />
      <NoDataBlock
        v-else-if="!documents.length"
        class="ml-6 mr-6"
        :label="labels[$route.name]"
        :document-type="types[$route.name]"
      >
        <template #content>
          <DpplPageLoader v-if="otherDocumentTypesLoading" />
          <div
            v-else-if="sortedByLargestTextTotals.length"
            class="mt-8"
          >
            <div
              v-for="({ mode, total }) in sortedByLargestTextTotals"
              :key="mode"
              class="result text-textDarkest"
            >
              Found
              <router-link :to="{ name: mode, query: otherDocumentsQuery(mode) }" class="text-textBrand">
                {{ total }}
                {{ types[mode] || 'Posts' }}
              </router-link>
              in {{ labels[mode] || '' }}
              {{ types[mode] || 'Posts' }} Library
            </div>
          </div>
        </template>
      </NoDataBlock>
      <div v-else class="document-list-width">
        <StickyListHeader
          v-if="documents.length"
          class="document-list-item-wrapper"
          :document-class="headerClassNames"
          :header-columns="headerColumns"
          :selected-sort="selectedSort"
          :sort-direction="sortDirection"
          @submit-sort="submitSort"
        />
        <LibraryList>
          <NoEditorWrapper
            v-for="document in documentsWithItems"
            :key="document.id"
            :redirect-route="{ query: { preview: document.id, type: 'document' } }"
          >
            <LibraryListItemWrapper class="document-list-item-wrapper">
              <router-link
                :to="{ name: 'editor-view', params: { id: document.id } }"
                class="document-list-item-link"
                @click.stop.prevent="$router.replace({ query: { preview: document.id, type: 'document' } })"
              >
                <FlagshipDocumentListItem
                  v-if="documentType === 'flagship'"
                  :class="[`item-${documentType}`, isAdmin ? 'is-admin' : '']"
                  :document="document"
                  :is-admin="isAdmin"
                  @library-req-report-track="libraryReqReportLinkClick(document)"
                  @flagship-toggle-update="onFlagshipToggleUpdate"
                />
                <DraftDocumentListItem
                  v-if="documentType === 'draft'"
                  :class="[`item-${documentType}`, integrated ? 'integrated' : '']"
                  :document="document"
                  :integrated="integrated"
                />
                <PublishedDocumentListItem
                  v-if="documentType === 'published'"
                  :class="[`item-${documentType}`, integrated ? 'integrated' : '']"
                  :document="document"
                  :integrated="integrated"
                />
                <UnpublishedDocumentListItem
                  v-if="documentType === 'unpublished'"
                  :class="[`item-${documentType}`, integrated ? 'integrated' : '']"
                  :document="document"
                  :integrated="integrated"
                />
                <EmailDocumentListItem
                  v-if="documentType === 'email'"
                  :class="`item-${documentType}`"
                  :document="document"
                />
              </router-link>
              <ThreeDotsMenu
                :menu-items="document.items"
                :document="document"
                @click="handleMenuItemClick"
              />
            </LibraryListItemWrapper>
          </NoEditorWrapper>
        </LibraryList>
      </div>
      <StickyPagination
        v-if="totalPages > 1"
        :model-value="page"
        :length="totalPages"
        @update:model-value="(value) => sendFilterRequest({ pageNumber: value, router: $router })"
      />
    </div>
    <ShareModal
      :show="showShareJobModal"
      :job-id="jobToShare"
      title="Share This Job"
      @close="closeModal('share')"
    />
    <DuplicateModal
      :show="showDuplicateJobModal"
      :doc-title="jobToDuplicate ? jobToDuplicate.title : ''"
      :document-type="documentType === 'flagship' ? 'flagship' : jobToDuplicate?.documentType"
      :new-document-type="documentType === 'flagship' ? 'draft job post' : jobToDuplicate?.documentType"
      @close="closeModal('duplicate')"
      @duplicate="handleDuplicateJob($event)"
    />
    <DeleteModal
      :show="showDeleteJobModal"
      :name="jobToDelete ? jobToDelete.title : ''"
      :document-type="documentType === 'flagship' ? 'flagship job' : 'job post'"
      :detailed-text="documentType === 'flagship' ? 'flagship job' : 'job post'"
      @close="closeModal('delete')"
      @delete="handleDeleteJob"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia';
import LibrariesSkeletonLoader from '@/components/shared/SkeletonLoaders/LibrariesSkeletonLoader.vue';
import LibraryList from '@/components/shared/LibraryList.vue';
import LibraryListItemWrapper from '@/components/shared/LibraryListItemWrapper.vue';
import NoDataBlock from '@/components/library/NoDataBlock.vue';
import NoFlagshipPosts from '@/components/library/NoFlagshipPosts.vue';
import ShareModal from '@/components/editor/ShareModal.vue';
import DeleteModal from '@/components/shared/DeleteModal.vue';
import DuplicateModal from '@/components/shared/DuplicateModal.vue';
import FlagshipDocumentListItem from '@/components/library/documentListItems/FlagshipDocumentListItem.vue';
import DraftDocumentListItem from '@/components/library/documentListItems/DraftDocumentListItem.vue';
import PublishedDocumentListItem from '@/components/library/documentListItems/PublishedDocumentListItem.vue';
import UnpublishedDocumentListItem from '@/components/library/documentListItems/UnpublishedDocumentListItem.vue';
import EmailDocumentListItem from '@/components/library/documentListItems/EmailDocumentListItem.vue';
import ThreeDotsMenu from '@/components/shared/ThreeDotsMenu.vue';
import StickyPagination from '@/components/shared/StickyPagination.vue';
import DpplPageLoader from '@/components/datapeople/DpplPageLoader.vue';
import NoEditorWrapper from '@/components/editor/NoEditorWrapper.vue';
import StickyListHeader from '@/components/library/StickyListHeader.vue';
import { useAppLoading } from '@/stores/app/loading';
import { useAppAuthData } from '@/stores/app/authData';
import { useAppPagination } from '@/stores/app/pagination';
import { useLibrary } from '@/stores/library/library';
import { useAppBase } from '@/stores/app/base';
import { useFilters } from '@/stores/filters/filters';
import { useSubMenu } from '@/stores/filters/subMenu';
import { useTracking } from '@/stores/tracking/tracking';
import { getDefaultPostSort } from '@/stores/library/helpers';
import { getQueryParamsFromURL } from '@/helpers/routing';
import { subscribeToFilterUpdates, unsubscribeToFilterUpdates } from '@/helpers/filterDispatcher';
import { stickyHeadersDocumentList } from '@/helpers/constants/stickyHeaders';
import {
  EDIT_ICON, DUPLICATE_ICON, DELETE_ICON, OPEN_IN_ICON, VIEW_ICON,
} from '@/helpers/constants/librariesMenuIcons';
import { getDefaultReqSort } from '@/stores/requisitions/helpers';

export default {
  name: 'DocumentList',
  components: {
    LibraryList,
    LibraryListItemWrapper,
    NoEditorWrapper,
    StickyPagination,
    NoDataBlock,
    NoFlagshipPosts,
    ShareModal,
    DeleteModal,
    DuplicateModal,
    LibrariesSkeletonLoader,
    DpplPageLoader,
    StickyListHeader,
    FlagshipDocumentListItem,
    DraftDocumentListItem,
    PublishedDocumentListItem,
    UnpublishedDocumentListItem,
    EmailDocumentListItem,
    ThreeDotsMenu,
  },
  props: {
    documentType: {
      type: String,
      default: 'published',
    },
  },
  data() {
    return {
      showShareJobModal: false,
      jobToShare: null,
      jobToDelete: null,
      jobToDuplicate: null,
      showDeleteJobModal: false,
      showDuplicateJobModal: false,
      labels: {
        published: 'Published',
        unpublished: 'Unpublished',
        draft: 'Draft',
        flagship: 'Flagship',
        'closed-requisitions': 'Closed',
        'open-requisitions': 'Open',
        'draft-requisitions': 'Draft',
      },
      types: {
        email: 'Emails',
        'draft-requisitions': 'Requisitions',
        'open-requisitions': 'Requisitions',
        'closed-requisitions': 'Requisitions',
      },
    };
  },
  computed: {
    ...mapState(useAppLoading, ['pageLoading', 'listLoading', 'sidebarLoading']),
    ...mapState(useAppBase, ['browserBackOrForwardNavigation']),
    ...mapState(useFilters, ['myJobsEnabled', 'searchTerm', 'activeFilters']),
    ...mapState(useLibrary, [
      'documents',
      'applicationReportingEnabled',
      'otherDocumentTypesLoading',
      'otherDocumentTypeTotals',
    ]),
    ...mapState(useAppPagination, ['totalPages', 'page']),
    ...mapState(useAppAuthData, ['integrated', 'authData', 'isAdmin']),
    ...mapState(useSubMenu, ['selectedSubMenuOption']),
    defaultSort() {
      return getDefaultPostSort(this.documentType);
    },
    selectedSort() {
      return this.selectedSubMenuOption?.replace('-', '') || this.defaultSort;
    },
    sortDirection() {
      return this.selectedSubMenuOption?.startsWith('-') ? 'desc' : 'asc';
    },
    atsName() { return this.authData.atsName; },
    headerColumns() {
      const headersPerType = {
        draft: [
          'score',
          'title',
          'lastEdited',
          'reqId',
          'location',
          this.integrated ? 'department' : '',
          this.integrated ? 'createdIn' : '',
        ],
        published: [
          'score',
          'title',
          'daysLive',
          'reqId',
          'location',
          this.integrated ? 'department' : '',
        ],
        unpublished: [
          'score',
          'title',
          'unpublished',
          'reqId',
          'location',
          this.integrated ? 'department' : '',
        ],
        flagship: [
          'score',
          'title',
          'lastEdited',
          'created',
          'location',
          'metadata',
          this.isAdmin ? 'visibility' : '',
        ],
        email: ['title', 'lastEdited'],
      }[this.documentType];

      return headersPerType
        .map((header) => stickyHeadersDocumentList[header])
        .filter(Boolean);
    },
    headerClassNames() {
      const integrated = this.integrated ? 'integrated' : '';
      const isAdmin = (this.isAdmin && this.documentType === 'flagship') ? 'is-admin' : '';

      return `item-${this.documentType} ${integrated} ${isAdmin}`;
    },
    sortedByLargestTextTotals() {
      const order = ['draft-requisitions', 'open-requisitions', 'closed-requisitions', 'unpublished', 'published', 'flagship', 'draft', 'email'];

      return [...this.otherDocumentTypeTotals].sort(({ mode: aMode }, { mode: bMode }) => {
        const aIndex = order.indexOf(aMode);
        const bIndex = order.indexOf(bMode);

        return aIndex - bIndex;
      }).filter(({ total }) => Boolean(total));
    },
    documentsWithItems() {
      return this.documents.map((doc) => ({ ...doc, items: this.getDocumentMenuItems(doc) }));
    },
    libraryLoading() {
      return this.sidebarLoading || this.pageLoading || this.listLoading;
    },
  },
  watch: {
    browserBackOrForwardNavigation(newValue) {
      /**
       * Verifies the correct path in case this is triggered
       * on a back button click to a different page
       */
      if (newValue && window.location.pathname === `/app/${this.documentType}`) {
        const searchParams = getQueryParamsFromURL();

        this.getDocuments({
          routeQuery: searchParams,
          isBrowserNavigated: true,
        });
      }
    },
  },
  async mounted() {
    subscribeToFilterUpdates(this.loadDocuments);
    await this.getDocuments({
      routeQuery: this.$route.query,
      isBrowserNavigated: this.browserBackOrForwardNavigation,
    });
  },
  beforeUnmount() {
    this.setSelectedSubMenuOption(null);
    unsubscribeToFilterUpdates();
  },
  methods: {
    ...mapActions(useFilters, ['sendFilterRequest', 'submitSubMenu']),
    ...mapActions(useLibrary, ['loadDocuments', 'deleteDocument', 'duplicateDocument', 'toggleFlagshipHidden']),
    ...mapActions(useSubMenu, ['setSelectedSubMenuOption']),
    ...mapActions(useTracking, ['libraryReqReportLinkClick']),
    otherDocumentsQuery(mode) {
      let sort = getDefaultPostSort(mode);

      if (mode.includes('requisitions')) {
        sort = getDefaultReqSort(mode.replace('-requisitions', ''));
      }

      return {
        ...this.$route.query,
        sort,
      };
    },
    submitSort(selectedId) {
      if (selectedId === this.selectedSort) {
        const optionId = this.selectedSubMenuOption.includes('-') ? this.selectedSort : `-${this.selectedSort}`;

        this.submitSubMenu({ optionId, router: this.$router });
      } else {
        const optionId = selectedId === this.defaultSort.replace('-', '') ? this.defaultSort : selectedId;

        this.submitSubMenu({ optionId, router: this.$router });
      }
    },
    onFlagshipToggleUpdate({ id, isHidden }) {
      this.toggleFlagshipHidden({
        id,
        isHidden: !isHidden,
        router: this.$router,
      });
    },
    async getDocuments({ routeQuery, isBrowserNavigated, selectedFilters }) {
      const newRouteQuery = { ...routeQuery };

      if (this.documentType === 'flagship' && newRouteQuery?.owned) {
        delete newRouteQuery.owned;
      }

      await this.loadDocuments({
        mode: this.documentType,
        routeQuery: newRouteQuery || {},
        router: this.$router,
        page: newRouteQuery.page,
        isBrowserNavigated,
        searchTerm: newRouteQuery?.q || '',
        selectedMyJobs: newRouteQuery?.owned,
        selectedFilters,
      });
    },
    upperCaseFirstLetter(str) {
      return str.charAt(0).toUpperCase() + str.slice(1);
    },
    closeModal(action) {
      const type = this.upperCaseFirstLetter(action);

      this[`show${type}JobModal`] = false;
      this[`jobTo${type}`] = null;
    },
    showModal(action, jobData) {
      const type = this.upperCaseFirstLetter(action);

      this[`show${type}JobModal`] = true;
      this[`jobTo${type}`] = jobData;
    },
    async handleDeleteJob() {
      this.showDeleteJobModal = false;
      const { id } = this.jobToDelete;
      const { documentType } = this;

      this.deleteDocument({ id, documentType });
      this.jobToDelete = null;
    },
    async handleDuplicateJob({ isPrivate, title }) {
      if (this.showDuplicateJobModal) {
        this.showDuplicateJobModal = false;
      }

      if (this.documentType === 'flagship') {
        useTracking().flagshipDuplicate({ origin: 'Library Page Dropdown' });
      }

      const newJobId = await this.duplicateDocument({
        jobId: this.jobToDuplicate.id,
        isPrivate,
        title,
        from: 'library-menu',
      });
      if (newJobId) {
        this.$router.push({ name: 'editor-view', params: { id: newJobId } });
      }
    },
    getDocumentMenuItems(document) {
      if (this.documentType === 'flagship') {
        const flagshipItems = [
          { text: 'View', icon: VIEW_ICON, value: 'edit' },
          { text: 'Create Draft from this Flagship Job', icon: DUPLICATE_ICON, value: 'duplicate' },
        ];

        if (this.isAdmin) {
          flagshipItems.push({ text: 'Delete', icon: DELETE_ICON, value: 'delete' });
        }

        return flagshipItems;
      }

      const baseItems = [
        { text: 'Edit', icon: EDIT_ICON, value: 'edit' },
        { text: 'Duplicate', icon: DUPLICATE_ICON, value: 'duplicate' },
      ];

      if (document.is_linked) {
        if (document.ats_edit_url && !['Datapeople Anywhere', 'Datapeople', 'Tapwage'].includes(document.platform)) {
          // Only integrated customers have their ATS as the platform name
          baseItems.push({ text: `Open in ${document.platform}`, icon: OPEN_IN_ICON, value: 'platform' });
        } else if (document.is_open && document.is_published) {
          // Non-integrated customers can only view currently published jobs on their career page
          if (document.job_board_url) {
            baseItems.push({ text: 'View Job on Job Board', icon: OPEN_IN_ICON, value: 'jobboard' });
          } else if (document.tapwage_url) {
            baseItems.push({ text: 'View Job on Tapwage.com', icon: OPEN_IN_ICON, value: 'tapwage' });
          }
        }
      } else if (this.isAdmin || document.is_private) { // only admins can delete public job
        baseItems.push({ text: 'Delete', icon: DELETE_ICON, value: 'delete' });
      }

      return baseItems;
    },
    handleMenuItemClick(value, job) {
      const { id } = job;
      const actions = {
        edit: () => {
          this.$router.push({ name: 'editor-view', params: { id } });
        },
        duplicate: () => {
          this.showModal(value, job);
        },
        share: () => {
          this.showModal(value, job.id);
        },
        delete: () => {
          this.showModal(value, job);
        },
        tapwage: () => {
          window.open(job.tapwage_url, '_blank', 'noopener,noreferrer');
        },
        jobboard: () => {
          window.open(job.job_board_url, '_blank', 'noopener,noreferrer');
        },
        greenhouse: () => {
          window.open(job.ats_edit_url, '_blank', 'noopener,noreferrer');
        },
        platform: () => {
          window.open(job.ats_edit_url, '_blank', 'noopener,noreferrer');
        },
      };

      if (actions[value]) {
        actions[value]();
      }
    },
  },
};
</script>

<style lang="less" scoped>
.document-list-width {
  min-width: 1100px;
  padding: 0 24px;
}

.document-list-item-wrapper {
  display: grid;
  grid-template-columns: 1fr 50px;
}

.document-list-item-link {
  color: rgb(var(--v-theme-textLight));

  &:visited {
    color: rgb(var(--v-theme-textLight));
  }
}

.document-list-item {
  :deep(.document-date) {
    font-size: 13px;
  }

  .flagship-visibility, :deep(.flagship-visibility) {
    text-align: left;
    display: flex;
    justify-content: flex-start;

    .v-label {
      max-width: initial;
      font-size: 13px;
    }
  }
}

// Drafts
.item-draft, :deep(.sticky-header-items.item-draft) {
  grid-template-columns: 80px minmax(230px, 1fr) 92px 116px minmax(120px, 184px);
}

.item-draft.integrated, :deep(.sticky-header-items.item-draft.integrated) {
  grid-template-columns: 80px minmax(230px, 1fr) 92px 116px repeat(2, minmax(120px, 184px)) 128px;
}

// Published
.item-published, :deep(.sticky-header-items.item-published) {
  grid-template-columns: 80px minmax(230px, 1fr) 92px 116px minmax(120px, 184px);
}

.item-published.integrated, :deep(.sticky-header-items.item-published.integrated) {
  grid-template-columns: 80px minmax(230px, 1fr) 92px 116px repeat(2, minmax(120px, 184px));
}

// Unpublished
.item-unpublished, :deep(.sticky-header-items.item-unpublished) {
  grid-template-columns: 80px minmax(230px, 1fr) 92px 116px minmax(120px, 184px);
}

.item-unpublished.integrated, :deep(.sticky-header-items.item-unpublished.integrated){
  grid-template-columns: 80px minmax(230px, 1fr) 92px 116px repeat(2, minmax(120px, 184px));
}

// Flagship
.item-flagship, :deep(.sticky-header-items.item-flagship) {
  grid-template-columns: 80px minmax(240px, 1fr) repeat(2, 92px) minmax(92px, 184px) 92px;
}

.item-flagship.is-admin, :deep(.sticky-header-items.item-flagship.is-admin) {
  grid-template-columns: 80px minmax(240px, 1fr) repeat(2, 92px) minmax(92px, 184px) 92px 134px;
}

// Email
.item-email, :deep(.sticky-header-items.item-email) {
  grid-template-columns: minmax(240px, 1fr) 130px;
}

:deep(.sortable-column.score),
:deep(.sticky-header-items.item-email) .sortable-column.title {
  padding-left: 24px;
}

.data-available-soon {
  text-align: center;
  font-size: 12px;
  line-height: 16px;
  color: rgb(var(--v-theme-textDarkest));
  overflow-wrap: break-word;
}

.result {
  font-size: 16px;
  line-height: 24px;
  font-weight: 700;
}

.read-more {
  color: rgb(var(--v-theme-textBrand));
}
</style>
